// ============================================================
// ExpressJwtWithRoles.jsx
//
// A branded, responsive course content page for the
// "RBAC with JWT" lesson.
//
// Author: ProDev Security Series
// Features: Middleware layering, Role-based logic, and
//           navigation bridge to the Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import VerifiedUserIcon       from "@mui/icons-material/VerifiedUser";
import SecurityIcon           from "@mui/icons-material/Security";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import ShieldIcon             from "@mui/icons-material/Shield";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressJwtWithRoles() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Security · Module 15"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        RBAC with <span>JWT Payload</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Role-Based Access Control (RBAC) allows you to restrict access based on user categories. By embedding roles directly into the JWT, we enable stateless authorization that is fast and secure.
                    </Typography>
                </Box>

                {/* 1. PAYLOAD ENRICHMENT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="1. Embedding Roles" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        When issuing a token, include the <code style={{color: BRAND.white}}>role</code> property. This data becomes part of the decoded object in your middleware.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const user = { id: 1, email: 'admin@prodev.com', role: 'admin' };", comment: "User data from DB" },
                        { cmd: "const token = jwt.sign(user, ACCESS_SECRET, { expiresIn: '15m' });", comment: "Sign payload with role" },
                    ]} />
                </Box>

                {/* 2. AUTHORIZATION MIDDLEWARE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="2. Authorization Middleware" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Create a higher-order function that verifies if the <code style={{color: BRAND.white}}>req.user.role</code> matches the required permissions for the route.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "function authorizeRoles(...allowedRoles) {", comment: "middleware/authorize.js" },
                        { cmd: "  return (req, res, next) => {", comment: "" },
                        { cmd: "    if (!req.user || !allowedRoles.includes(req.user.role)) {", comment: "" },
                        { cmd: "      return res.status(403).json({ error: 'Access Denied' });", comment: "" },
                        { cmd: "    }", comment: "" },
                        { cmd: "    next();", comment: "" },
                        { cmd: "  };", comment: "" },
                        { cmd: "}", comment: "" },
                    ]} />
                </Box>

                {/* 3. MIDDLEWARE LAYERING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ShieldIcon />} label="3. Middleware Chaining" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Always layer your middleware: first verify identity (<code style={{color: BRAND.white}}>authenticate</code>), then verify permissions (<code style={{color: BRAND.white}}>authorize</code>).
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const authenticate = require('./middleware/auth');", comment: "" },
                        { cmd: "const authorize = require('./middleware/authorize');", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "app.get('/admin/panel', authenticate, authorize('admin'), (req, res) => {", comment: "Admin Only" },
                        { cmd: "  res.send('Welcome, Authorized Admin');", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ROLE SUMMARY */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AdminPanelSettingsIcon />} label="Common Role Patterns" />
                    <Grid container spacing={2}>
                        {[
                            { role: "admin", desc: "Full CRUD access to all system resources.", color: "#f44336" },
                            { role: "editor", desc: "Can Create and Update, but restricted from Delete.", color: "#ff9800" },
                            { role: "user", desc: "Read-only access to their own profile and content.", color: "#4caf50" },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.role}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                                    <Typography sx={{ color: item.color, fontWeight: 800, fontSize: 14, mb: 0.5, textTransform: 'uppercase' }}>{item.role}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Learning Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Implement Role-Based Access Control using JWT payloads",
                            "Develop higher-order middleware for dynamic role validation",
                            "Apply middleware layering: authenticate → authorize",
                            "Handle 403 Forbidden responses for unauthorized roles",
                            "Identify best practices for server-side role derivation",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Never trust roles sent directly from the client frontend. Always derive the user's role from your database during login and verify the cryptographically signed JWT on every subsequent request.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
